import { paths } from '../routes/paths';

export type ActivityId = number;

/**
 * The timesheet's canonical activity catalogue (ClickUp CU-86bbwh5m2): 31
 * numeric ids, where 1-2 are intermediary activities, 3-14 have no subject,
 * and 15-31 are company-scoped (22 collection, 23 analysis, 24 deal
 * assessment).
 *
 * The names are copied **verbatim** from the `activity` column of the
 * `time_activity` seed in `migrations/sql/0295c_time_tracking.sql`, which is
 * the source of truth. They are duplicated here rather than fetched because
 * the label has to exist before the session's first event, and that event is
 * captured in posthog-js's `loaded` callback, long before any query could
 * resolve. `activity.test.ts` parses that migration and fails if the two lists
 * disagree, so the copy cannot drift silently -- do not "tidy" a string here.
 *
 * Ids are the stable key; a label is a display string and may be reworded.
 * Group and filter dashboards on `activity_id`, and use `activity_label` for
 * what a human reads.
 */
export const ACTIVITY_LABELS: Readonly<Record<ActivityId, string>> = {
  1: 'Identification and approach of new intermediaries',
  2: 'Relationships with existing intermediaries',
  3: 'Organization of own events, roundtables or dinners',
  4: 'Preparation of press releases',
  5: 'Maintenance of online presence',
  6: 'Preparation of sales / marketing slides',
  7: 'Participation in external deal sourcing conferences / events',
  8: 'Maintenance of Pipedrive master data',
  9: 'Maintenance and improvement of Pipedrive workflow logic',
  10: 'Data hygiene and updates',
  11: 'Management of standard process and templates',
  12: 'Recruiting, onboarding and training of team members',
  13: 'Pipeline reporting and KPI tracking',
  14: 'Weekly dealflow meetings and other internal meetings',
  15: 'Validation in Cortex',
  16: 'Direct outreach to target companies',
  17: 'Handling of inbound leads',
  18: 'Physical meetings with potential targets',
  19: 'Virtual meetings with potential targets',
  20: 'Meeting preparation, debriefing, and follow-up',
  21: 'NDA negotiation and execution',
  22: 'Collection of target company information',
  23: 'Analysis of target company information',
  24: 'Preparation of Deal Assessment file',
  25: 'Deal structuring modelling in TRD file: valuation, roll-over, minority stake, acquisition debt',
  26: 'Preparation and submission of indicative offer letter',
  27: 'Clarification and Q&A with target on the offer',
  28: 'Management presentation, attendance and coordination',
  29: 'Update and iteration of revised offer versions',
  30: 'Negotiation of indicative offer terms',
  31: 'Handover to Deal Execution Team upon acceptance',
};

// Derived from the labels rather than declared separately, so the id range
// this module validates against and the names it reports cannot disagree
// about which activities exist.
export const ACTIVITY_CATALOGUE_IDS: ReadonlyArray<ActivityId> = Object.keys(ACTIVITY_LABELS).map(Number);

// Never undefined: any pathname the map below does not recognise resolves
// here instead, matching UNCLASSIFIED_WORKSTREAM's contract in workstream.ts
// for the same reason -- a PostHog dashboard filtering on `activity_id` must
// never see a missing property.
export const UNCLASSIFIED_ACTIVITY = 'unclassified';

// The label side of the same fallback. Capitalised because it is display text
// and sits beside real catalogue names in a breakdown, where a lone lowercase
// row reads as a bug rather than as a category.
export const UNCLASSIFIED_ACTIVITY_LABEL = 'Unclassified';

export type Activity = ActivityId | typeof UNCLASSIFIED_ACTIVITY;

/**
 * What every PostHog event carries.
 *
 * `activity_candidates` is the *ambiguity* of the resolution, not a copy of
 * the mapping. When the activity is determined it holds exactly one id --
 * `[activity_id]` -- so a dashboard can read "this event is unambiguous"
 * straight off the array length. It holds two or more ids only where the
 * route genuinely cannot be narrowed, which is what makes a widened
 * `has(activity_candidates, X)` query meaningfully different from an
 * `activity_id = X` one. Emitting a fixed multi-id set on every event of a
 * page (as the first cut of this module did for /evaluation/company) makes
 * the property carry no information at all.
 *
 * Querying `activity_candidates` in HogQL: PostHog stores an array property
 * as `Nullable(String)` holding its JSON encoding, so
 * `has(properties.activity_candidates, '22')` silently matches nothing.
 * Parse it first:
 *
 *   has(JSONExtractArrayRaw(properties.activity_candidates), '22')
 *
 * `activity_id` is a plain scalar and needs no such handling; prefer it, and
 * reach for the candidates array only to widen a query deliberately.
 *
 * `activity_label` is the catalogue name for `activity_id`, sent so a chart
 * reads "Validation in Cortex" rather than "15". It is display text: group and
 * filter on `activity_id`, which is the stable key, and use the label only for
 * what a human sees. Two events with the same id always mean the same
 * activity; two events with the same label only do so until someone rewords a
 * catalogue row.
 */
export interface ActivityResolution {
  activity_id: Activity;
  activity_candidates: Activity[];
  activity_label: string;
}

/**
 * A route's activity.
 *
 * `id` is what the route resolves to on its own. `narrow` is for routes whose
 * page hosts several activities behind one pathname: it reads the UI state
 * the app already keeps in the query string and returns the activity actually
 * on screen. A route only needs `candidates` when it has no `narrow` and is
 * still genuinely ambiguous -- absent, the candidate set is just `[id]`.
 */
export interface ActivityMapping {
  readonly id: ActivityId;
  readonly candidates?: ReadonlyArray<ActivityId>;
  readonly narrow?: (params: URLSearchParams) => ActivityId | undefined;
}

export type ActivityRouteMap = ReadonlyArray<readonly [string, ActivityMapping]>;

// companyFinder's constants are bare segments (no leading slash) because they
// were written to be nested under a parent route; the routes they describe are
// top-level, so prefix the slash back on here rather than changing the shared
// paths.ts contract for this one caller -- workstream.ts does the same.
const SOFTWARE_CATEGORIES_PATH = `/${paths.companyFinder.softwareCategories}`;
const CUSTOMER_INDUSTRIES_PATH = `/${paths.companyFinder.customerIndustries}`;

/**
 * The company workspace's tab -> activity map.
 *
 * /evaluation/company is one pathname over twelve tabs, and the tabs are not
 * one activity: the Overview tab builds the deal assessment file (24), the
 * Mailing and Cold Call tabs are outreach (16), and the rest split between
 * collecting company information (22) and analysing it (23). EvaluationLayout
 * keeps the active tab in the URL as `?stab=`, so the activity is readable at
 * capture time and this route never has to fall back to a candidate set.
 *
 * Keys must stay in step with `TABS` in
 * pages/EvaluationSections/CompanySectionPage.tsx; activity.test.ts fails if
 * the two drift apart.
 */
export const COMPANY_WORKSPACE_TAB_ACTIVITIES: Readonly<Record<string, ActivityId>> = {
  overview: 24, // DFMPage -- generates and downloads the deal assessment file
  kpis: 23,
  people: 16, // Who to contact at the company, and when they were last reached
  product: 22,
  customers: 23,
  aireadiness: 23,
  network: 22,
  workforce: 23,
  files: 22,
  call: 20, // Call Assessment -- meeting preparation, debriefing and follow-up
  meetings: 20,
  todos: 20, // Follow-up work items for the company, same bucket as the Activities tab itself
  chat: 23, // The Slack channel binding is analysis material, same as KPIs/Customers/AI Readiness
  mailing: 16, // Direct outreach to target companies
  coldcall: 16,
  data: 10, // Data tab -- checking what the pipeline has analysed and whether it is current is data hygiene
  execution: 21, // Execution tab -- opens on its 01 NDA step; sub-steps with their own activity are in EXECUTION_STEP_ACTIVITIES
};

/**
 * Execution sub-steps (`?estep=`, read by tabs/ExecutionTab.tsx) that have a
 * catalogue activity of their own. A step not listed here -- NDA,
 * questionnaire, LOI, data room, or no `estep` at all -- keeps the tab's 21.
 * Only the unambiguous mapping is made; the others are a taxonomy call.
 */
export const EXECUTION_STEP_ACTIVITIES: Readonly<Record<string, ActivityId>> = {
  offer: 26, // 03 Indicative offer -- "Preparation and submission of indicative offer letter"
};

// EvaluationLayout falls back to the first tab when `?stab=` is absent or
// names a tab that does not exist, so the resolver has to make the same
// choice -- otherwise a bare /evaluation/company link and the page the user
// is actually looking at disagree about the activity.
const COMPANY_WORKSPACE_DEFAULT_TAB = 'overview';

function narrowCompanyWorkspace(params: URLSearchParams): ActivityId {
  const requested = params.get('stab');
  if (requested === 'execution') {
    const step = params.get('estep');
    const stepActivity = step !== null ? EXECUTION_STEP_ACTIVITIES[step] : undefined;
    if (stepActivity !== undefined) return stepActivity;
  }
  return (
    (requested !== null ? COMPANY_WORKSPACE_TAB_ACTIVITIES[requested] : undefined) ??
    COMPANY_WORKSPACE_TAB_ACTIVITIES[COMPANY_WORKSPACE_DEFAULT_TAB]
  );
}

// Route -> activity. Order does not matter: resolveActivityFromRoutes always
// picks the entry whose static path segments are the longest match for the
// current pathname, so a specific child route beats a shorter parent that
// also matches.
//
// Scope note. The catalogue is the *Origination* team's taxonomy, but an
// activity id describes the work on the page, not who is doing it, so the
// cockpit block below is mapped on the same basis as the rest. The cockpit is
// PortCo-facing and its users are not the Origination team, so any query that
// means "hours the Origination team spent" must exclude it -- the existing
// `workstream` super property already separates it as 'buy-and-build', so
// filter on that rather than reading these ids as timesheet hours. The block
// is kept contiguous so the decision can be reversed in one edit.
export const ACTIVITY_ROUTES: ActivityRouteMap = [
  // -- 10 Data hygiene and updates -----------------------------------------
  [paths.dataQuality.main, { id: 10 }],
  [paths.dataQuality.review, { id: 10 }],
  [paths.admin.dataEditor, { id: 10 }],
  [paths.crawlerOps.main, { id: 10 }],
  [paths.crawlerOps.crawlQueue, { id: 10 }],
  [paths.crawlerOps.enrichmentPerformance, { id: 10 }],
  [paths.crawlerOps.swClassifier, { id: 10 }],

  // -- 8/9 Pipedrive master data and workflow logic -------------------------
  [paths.pipedriveActivity, { id: 8 }],
  [paths.crawlerOps.pipedrive, { id: 9 }],

  // -- 11 Management of standard process and templates ----------------------
  [paths.settings, { id: 11 }],
  [paths.dealAssessmentTemplate, { id: 11 }],
  [paths.aiThreatDesign, { id: 11 }],
  [paths.scheduledTasks, { id: 11 }],
  [paths.meetingTemplates, { id: 11 }],
  [paths.featureToggles, { id: 11 }],
  [paths.fileConventionRules, { id: 11 }],
  [paths.dealLostReasons, { id: 11 }],
  [paths.dealStages, { id: 11 }],
  [paths.admin.questionnaires, { id: 11 }],

  // -- 13 Pipeline reporting and KPI tracking -------------------------------
  [paths.statistics, { id: 13 }],
  [paths.appInsight, { id: 13 }],
  [paths.crawlerOps.dashboard, { id: 13 }],
  [paths.crawlerOps.mlStats, { id: 13 }],
  [paths.crawlerOps.signalTracking, { id: 13 }],
  [paths.crawlerOps.userActivity, { id: 13 }],

  // -- 15 Validation in Cortex ----------------------------------------------
  [paths.validation, { id: 15 }],
  [paths.validationV2, { id: 15 }],
  [paths.crawlerInbox, { id: 15 }],

  // -- 16 Direct outreach to target companies -------------------------------
  [paths.evaluation.outreach, { id: 16 }],

  // -- 20 Meeting preparation, debriefing and follow-up ---------------------
  [paths.meetings, { id: 20 }],
  [paths.evaluation.companyMeetings, { id: 20 }],
  [paths.survey, { id: 20 }],

  // -- 22 Collection of target company information --------------------------
  [paths.search, { id: 22 }],
  [paths.pageSearch, { id: 22 }],
  [SOFTWARE_CATEGORIES_PATH, { id: 22 }],
  [CUSTOMER_INDUSTRIES_PATH, { id: 22 }],
  [paths.transactions, { id: 22 }],
  [paths.transactionsInvestors, { id: 22 }],
  [paths.investorsGraph, { id: 22 }],
  [paths.dealAnnouncements, { id: 22 }],
  [paths.evaluation.startups, { id: 22 }],
  [paths.evaluation.companyFiles, { id: 22 }],
  [paths.evaluation.companyAnnotations, { id: 22 }],
  [paths.evaluation.bbLists, { id: 22 }],
  [paths.evaluation.dealScout, { id: 22 }],
  [paths.aiUpload, { id: 22 }],

  // -- 23 Analysis of target company information ----------------------------
  [paths.evaluation.market, { id: 23 }],
  [paths.evaluation.marketResearch, { id: 23 }],
  [paths.evaluation.marketSegments, { id: 23 }],
  [paths.evaluation.techAssessment, { id: 23 }],
  [paths.evaluation.aiThreatIndex, { id: 23 }],
  [paths.evaluation.customerSegment, { id: 23 }],
  [paths.benchmarking, { id: 23 }],
  [paths.industryBenchmarks, { id: 23 }],
  [paths.research.industryBenchmarks, { id: 23 }],
  [paths.research.industryCodePilot, { id: 23 }],

  // -- 24 Preparation of Deal Assessment file -------------------------------
  // The tabbed company workspace. `narrow` reads the active tab, so this
  // route reports the tab's activity rather than a fixed 22/23/24 set; `id`
  // is the Overview tab's activity, which is what a bare link resolves to.
  [paths.evaluation.company, { id: 24, narrow: narrowCompanyWorkspace }],
  // Legacy standalone DFM route: DfmRedirect forwards it to the workspace
  // Overview tab preserving the query string, so it resolves the same way.
  [paths.evaluation.dfmStandalone, { id: 24 }],
  [paths.evaluation.dueDiligence, { id: 24 }],
  // The /evaluation index is the only route left that cannot be narrowed: it
  // is the section landing page and the user has not yet said which of
  // collection, analysis or deal assessment they came to do.
  [paths.evaluation.evaluation, { id: 22, candidates: [22, 23, 24] }],

  // -- 25 Deal structuring modelling (valuation, roll-over, debt) ------------
  [paths.research.valuationModel, { id: 25 }],

  // The CRM deal funnel, an internal-app route since the board moved out of the
  // cockpit tree. A kanban of deals by stage, so it reports the same activity
  // as the cockpit's own pipeline board below.
  [paths.crmDealFunnel, { id: 13 }],

  // -- Cockpit (PortCo-facing buy-and-build) -- see the scope note above -----
  [paths.cockpit, { id: 22 }],
  // Not a paths.ts constant any more: /cockpit/deals is now only the forwarding
  // stub left behind by that move (DealFunnelMoved in CockpitApp.tsx). It still
  // fires a section event, so it is mapped to the funnel's activity rather than
  // left to report the /cockpit parent's.
  ['/cockpit/deals', { id: 13 }],
  ['/cockpit/targets', { id: 22 }],
  ['/cockpit/portcos', { id: 22 }],
  ['/cockpit/origination', { id: 22 }],
  ['/cockpit/inbox', { id: 17 }], // Handling of inbound leads
  ['/cockpit/pipeline', { id: 13 }],
  ['/cockpit/reports', { id: 13 }],
  ['/cockpit/settings', { id: 11 }],

  // Deliberately unmapped, resolving to UNCLASSIFIED_ACTIVITY. These are not
  // gaps in the map -- none of them is one of the 31 catalogue rows, and
  // guessing an id for them would put invented hours into the vocabulary the
  // timesheet reports against:
  //   paths.main ('/')     PhoneHomeRedirect, a transitional route nobody works on
  //   /sign-in, /cockpit/sign-in, /access-denied  authentication and refusal
  //   paths.chat           the assistant, whose activity is whatever the user asks it
  //   paths.notifications  a cross-activity inbox
  //   paths.organization.members, paths.platformAdmin*
  //                        platform administration, outside the origination catalogue
  //   paths.gdriveRelink   a one-shot maintenance link
  // A rising unclassified share that is NOT one of these is the signal that
  // the map has drifted behind the routes.
];

function pathSegments(path: string): string[] {
  return path.split('/').filter(Boolean);
}

// A route pattern's static prefix is everything before its first
// react-router param placeholder (a segment starting with ":").
function staticPrefixSegments(routePath: string): string[] {
  const segments = pathSegments(routePath);
  const paramIndex = segments.findIndex(segment => segment.startsWith(':'));
  return paramIndex === -1 ? segments : segments.slice(0, paramIndex);
}

function isPrefixOf(prefix: string[], candidate: string[]): boolean {
  if (prefix.length > candidate.length) return false;
  return prefix.every((segment, index) => segment === candidate[index]);
}

// Every id this module can emit is asserted to be in the catalogue by
// activity.test.ts, so the fallback is unreachable in practice. It names the
// id rather than saying "Unclassified" anyway: a label that contradicts the
// `activity_id` beside it would send a reader looking for the wrong bug.
function labelFor(id: ActivityId): string {
  return ACTIVITY_LABELS[id] ?? `Activity ${id}`;
}

/**
 * Resolves a pathname against a route->activity map using the same
 * longest-prefix / parent-wins rule as resolveWorkstream: among every route
 * whose static segments prefix the pathname, the one with the most segments
 * decides the mapping, so a specific child route always beats a shorter
 * parent that also matches. Takes the route map as an argument (rather than
 * closing over ACTIVITY_ROUTES) so the matching rule itself can be unit
 * tested against a small map, independent of the production route list.
 *
 * `search` is the location's query string. It is only read by a matched
 * route's own `narrow`, so a caller that has no query string to give can
 * leave it out and still get the route-level answer.
 */
export function resolveActivityFromRoutes(pathname: string, routes: ActivityRouteMap, search = ''): ActivityResolution {
  const candidate = pathSegments(pathname);

  let best: { length: number; mapping: ActivityMapping } | null = null;
  for (const [routePath, mapping] of routes) {
    const prefix = staticPrefixSegments(routePath);
    // An empty prefix (the root path "/") only matches the root pathname
    // itself -- otherwise every pathname would vacuously match it.
    if (prefix.length === 0 ? candidate.length !== 0 : !isPrefixOf(prefix, candidate)) continue;
    if (!best || prefix.length > best.length) {
      best = { length: prefix.length, mapping };
    }
  }

  if (!best) {
    return {
      activity_id: UNCLASSIFIED_ACTIVITY,
      activity_candidates: [UNCLASSIFIED_ACTIVITY],
      activity_label: UNCLASSIFIED_ACTIVITY_LABEL,
    };
  }

  const narrowed = best.mapping.narrow?.(new URLSearchParams(search));
  if (narrowed !== undefined) {
    return { activity_id: narrowed, activity_candidates: [narrowed], activity_label: labelFor(narrowed) };
  }

  const activity_id = best.mapping.id;
  return {
    activity_id,
    activity_candidates: [...(best.mapping.candidates ?? [activity_id])],
    activity_label: labelFor(activity_id),
  };
}

/**
 * Resolves a React Router location to the timesheet activity PostHog should
 * tag its events with: a primary activity_id plus the activity_candidates set
 * the location could plausibly represent. Never returns undefined -- an
 * unmapped pathname resolves to UNCLASSIFIED_ACTIVITY.
 *
 * Pass `search` (location.search) wherever it is available: it is what lets a
 * route with several activities behind one pathname report the one actually
 * on screen instead of a candidate set.
 */
export function resolveActivity(pathname: string, search = ''): ActivityResolution {
  return resolveActivityFromRoutes(pathname, ACTIVITY_ROUTES, search);
}
